import { signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { Router } from '@angular/router';
import { AppTypeService, ParticipantAuthKeySource, WireApiService } from '../services';
import {
  ActiveConversationStore,
  ConversationStore,
  ErrorStore,
  MessageStore,
  TenantStore,
  UserStore,
} from './';
import { logger } from '../utils';
import { ConversationTypeStore } from './';
import {
  AuthenticationOptions,
  GetUserPreferenceRequest,
  ResetPasswordRequest,
  SetPasswordRequest,
} from '@clientwire/api';
import { UserPreferencesStore } from './user-preferences.store';

export type IdentityType = 'user' | 'participant' | null;

type IdentityState = {
  identityType: IdentityType;
  clientParticipantId: string | null;
  clientConversationId: string | null;
};

const initialState: IdentityState = {
  identityType: null,
  clientParticipantId: null,
  clientConversationId: null,
};

export type IdentityStore = InstanceType<typeof IdentityStore>;

export const IdentityStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withDevtools('identity-store'),
  withState(initialState),
  withMethods((store) => {
    const wireApiService = inject(WireApiService);
    const tenantStore = inject(TenantStore);
    const router = inject(Router);
    const appType = inject(AppTypeService);
    const userStore = inject(UserStore);
    const userPreferencesStore = inject(UserPreferencesStore);
    const activeConversationStore = inject(ActiveConversationStore);
    const conversationStore = inject(ConversationStore);
    const messageStore = inject(MessageStore);
    const conversationTypeStore = inject(ConversationTypeStore);
    const errorStore = inject(ErrorStore);

    return {
      logout() {
        wireApiService.logout();
        userStore.clearUser();
        userPreferencesStore.clearUserPreferences();
        activeConversationStore.clearActiveConversations();
        conversationStore.clearConversations();
        messageStore.clearMessages();
        conversationTypeStore.clearConversationTypes();

        this.clearIdentityStore();

        if (appType.type() === 'main') {
          router.navigate(['/login']);
        }
      },

      setIdentityType(type: IdentityType) {
        updateState(store, 'Set identity type', { identityType: type });
      },

      async signInWithEmailAndPassword(email: string, password: string) {
        try {
          const tenantId = appType.tenantId() || tenantStore.id();
          await wireApiService.signInWithEmailAndPassword(tenantId, email, password);
          await this.getUser();
          updateState(store, 'Set identity type', { identityType: 'user' });
          router.navigate(['/']);
        } catch (error: unknown) {
          try {
            await errorStore.addErrorResponse(error);
          } catch {
            throw new Error('Error signing in with email and password');
          }
          console.error('Error signing in with email and password', error);
        }
      },

      async signInWithParticipantAuthKey(participantAuthKey: ParticipantAuthKeySource) {
        try {
          // HACK
          if (!tenantStore.id()) {
            await this.getTenantConfig();
          }
          await wireApiService.signInWithParticipantAuthKey(tenantStore.id(), participantAuthKey);
          const clientParticipant = await this.getClientParticipant();
          updateState(store, 'Set identity type', {
            identityType: 'participant',
            clientParticipantId: clientParticipant.id,
            clientConversationId: clientParticipant.conversationId,
          });
        } catch (error) {
          logger.error('Error signing in with participant auth key', error);
          try {
            await errorStore.addErrorResponse(error, 'global');
          } catch {
            await errorStore.addErrorResponse({ code: 'error.identity.token' }, 'global', false);
          }
        }
      },

      async signInWithPassthroughToken(
        token: string | (() => string) | (() => string | Promise<string>) | Promise<string>
      ) {
        try {
          await wireApiService.signInWithPassthroughToken(appType.tenantId(), token);
          await this.getUser();
          updateState(store, 'Set identity type', { identityType: 'user' });
        } catch (error) {
          logger.error('Error signing in with passthrough token', error);
          try {
            await errorStore.addErrorResponse(error, 'global');
          } catch {
            await errorStore.addErrorResponse({ code: 'error.identity.token' }, 'global', false);
          }
        }
      },

      async getUser() {
        try {
          const response = await wireApiService.usersApi.getCurrentUser();
          userStore.setUser(response);
          await this.getUserPreferences({ userId: response.id });
        } catch (error) {
          try {
            await errorStore.addErrorResponse(error, 'global');
          } catch {
            await errorStore.addErrorResponse({ code: 'error.identity.getUser' }, 'global', false);
          }
        }
      },

      async getUserPreferences(request: GetUserPreferenceRequest) {
        try {
          const response = await wireApiService.userPreferencesApi.getUserPreference(request);
          userPreferencesStore.setUserPreferences(response);
        } catch (error) {
          try {
            await errorStore.addErrorResponse(error, 'inline');
          } catch {
            throw error;
          }
        }
      },

      async getClientParticipant() {
        return await wireApiService.participantsApi.getClientParticipantHimself();
      },

      async getTenantConfig() {
        if (!tenantStore.id()) {
          const fullDomain = window.location.host.split(':')[0];
          if (fullDomain) {
            await tenantStore.getConfigByDomain(fullDomain);
          } else {
            throw new Error('No domain found');
          }
        }
      },
      async resetPassword(resetPasswordRequest: ResetPasswordRequest) {
        try {
          await wireApiService.passwordResetApi.resetPassword({
            tenantId: tenantStore.id(),
            resetPasswordRequest: resetPasswordRequest,
          });
        } catch (error) {
          try {
            await errorStore.addErrorResponse(error, 'inline');
          } catch {
            throw error;
          }
        }
      },
      async setPassword(setPasswordRequest: SetPasswordRequest) {
        await wireApiService.passwordResetApi.setPassword({
          tenantId: tenantStore.id(),
          setPasswordRequest: setPasswordRequest,
        });
      },
      async getSignInOptions(email: string): Promise<AuthenticationOptions> {
        return await wireApiService.getSignInOptions({ email: email, tenantId: tenantStore.id() });
      },

      clearIdentityStore() {
        updateState(store, 'Clear identity store', {
          identityType: null,
          clientParticipantId: null,
        });
      },
    };
  }),

  withHooks((store) => {
    const wireApiService = inject(WireApiService);
    const errorStore = inject(ErrorStore);
    return {
      onInit: async () => {
        wireApiService.onAuthenticationError$.subscribe(async (event) => {
          if (event && event.reason === 'Could not refresh tokens') {
            await errorStore.addErrorResponse(
              { code: 'error.identity.refresh_token' },
              'global',
              false
            );
          }
          if (appType.type() !== 'embed') {
            store.logout();
          }
        });

        const appType = inject(AppTypeService);
        if (appType.type() === 'main') {
          if (wireApiService.hasCredentials) {
            await wireApiService.signInWithStoredCredentials();
            updateState(store, 'Set identity type', { identityType: 'user' });
            await store.getUser();
          }
        }
      },
    };
  })
);
